import { z } from 'zod';
import type { StateDimension, StateFact } from '@shared/schemas/flow-contact-state';
import {
  GONG_FLOW_LABEL,
  QUEUED_LABEL,
  GIFT_LABEL,
  RECENT_CAMPAIGN_LABEL,
  UPCOMING_CAMPAIGN_LABEL,
  SUGGESTED_INVITE_LABEL,
  RECENT_TASK_LABEL,
  RECENT_REQUEST_LABEL,
  WARM_INTRO_LABEL,
  RECENT_QUEUE_SEND_LABEL,
  pillLabelForFact,
  isGongLinkedOpenTaskFact,
} from '@shared/flow/pill-variants';

/**
 * Pure (React-free, Glaze-free) state-fact presentation module — the shared
 * source of truth for the fact→label/icon/key derivation that BOTH V1's
 * `StatePills.tsx` (`StateFactCard`) and V2's future `EntityStateFactCard`
 * (U13) read (plan 005 U1c lift). Lifted verbatim out of `StatePills.tsx`
 * with zero behavior change — the doc comments below carry the same product
 * history those functions had there.
 *
 * `pillLabelForFact` / `isGongLinkedOpenTaskFact` / `RECENT_TASK_LABEL` are
 * re-exported here (their real home is `src/shared/flow/pill-variants.ts`,
 * a server+frontend-shared module) purely so every consumer of THIS shared
 * presentation surface — V1 and V2 alike — imports them from one place
 * instead of two.
 *
 * Raw-palette Tailwind `bg`/`tx` classes deliberately do NOT cross into this
 * module (plan 005 D9 Glaze rule — no raw palette outside `shell-next`'s own
 * token usage, and this module is imported BY `shell-next` code) — V1 keeps
 * its own bg/tx map locally in `StatePills.tsx`; a Glaze surface maps
 * dimension → semantic token itself. Only `icon` + `label` (the parts both
 * surfaces render identically) are shared.
 */

export { pillLabelForFact, isGongLinkedOpenTaskFact, RECENT_TASK_LABEL };

/**
 * Unified display label for BOTH open-task pill variants — a direct/live SA
 * ("Live Task", wire `pill.label`) and a Gong-linked SA ("Gong Task", wire
 * `pill.label`) — per Spencer's 2026-07-30 decision: the ranking distinction
 * between the two stays (wire `pill.label` is still 'Live Task' vs
 * 'Gong Task' and still drives `PILL_DIMENSION_PRIORITY` ranking, untouched),
 * but the DISPLAYED text and styling are unified so both read as one
 * "Open Task" signal to the rep.
 */
export const OPEN_TASK_LABEL = 'Open Task';

export interface DimensionPresentation {
  icon: string;
  label: string;
}

/**
 * Per-dimension DEFAULT presentation (icon + label only — no bg/tx, see
 * file header). Mirrors V1's local `DIMENSION_STYLES` map one-for-one minus
 * the raw-palette fields.
 */
export const DIMENSION_STYLES: Record<StateDimension, DimensionPresentation> = {
  in_flow: { icon: '⚡', label: GONG_FLOW_LABEL },
  invited: { icon: '✉', label: RECENT_CAMPAIGN_LABEL },
  open_task: { icon: '☑', label: OPEN_TASK_LABEL },
  queued: { icon: '●', label: QUEUED_LABEL },
  gifted: { icon: '🎁', label: GIFT_LABEL },
  recent_request: { icon: '●', label: RECENT_REQUEST_LABEL },
  warm_intro: { icon: '🤝', label: WARM_INTRO_LABEL },
  recent_queue_send: { icon: '🚩', label: RECENT_QUEUE_SEND_LABEL },
};

/**
 * Fix A (Spencer QA — "suggested and recent invite are two different types.
 * It should just say suggested invite instead of recent.") variant, "Upcoming
 * Campaign" variant (same icon family as the default `invited` presentation,
 * only the label differs), and the closed-task ("Recent Task") variant of
 * `open_task` — same three overrides `factCardStyle` below has always
 * branched to, now colocated with it.
 */
const SUGGESTED_INVITE_STYLE: DimensionPresentation = { icon: '○', label: SUGGESTED_INVITE_LABEL };
const UPCOMING_CAMPAIGN_STYLE: DimensionPresentation = { icon: DIMENSION_STYLES.invited.icon, label: UPCOMING_CAMPAIGN_LABEL };
const RECENT_TASK_STYLE: DimensionPresentation = { icon: '✓', label: RECENT_TASK_LABEL };

/**
 * The presentation for one `StateFactCard` — a `StateFact` carries no
 * `label` field (only pills do), so this calls the shared `pillLabelForFact`
 * (the SAME derivation the server's adapters use as their `pillLabel` hook)
 * to find out which variant this fact is, instead of re-parsing `detail`
 * itself. `recent_request` has no variant here — a staged value renders
 * with the same neutral presentation as a plain action.
 */
export function factCardStyle(fact: StateFact): DimensionPresentation {
  if (fact.dimension === 'invited') {
    const label = pillLabelForFact(fact);
    if (label === SUGGESTED_INVITE_LABEL) return SUGGESTED_INVITE_STYLE;
    if (label === UPCOMING_CAMPAIGN_LABEL) return UPCOMING_CAMPAIGN_STYLE;
    // else → default DIMENSION_STYLES.invited ('Recent Campaign')
  }
  if (fact.dimension === 'open_task') {
    // Closed wins FIRST regardless of linkage — mirrors
    // signalActionsSource's pillLabel (contact-state-sources.ts): a closed
    // gong-linked task is still "Recent Task". An OPEN gong-linked task no
    // longer gets its own presentation (Spencer, 2026-07-30
    // display-unification decision) — it falls through to the default
    // DIMENSION_STYLES.open_task below, same as a direct open task
    // ("Open Task", ☑, no ⚡).
    if (pillLabelForFact(fact) === RECENT_TASK_LABEL) return RECENT_TASK_STYLE;
  }
  return DIMENSION_STYLES[fact.dimension];
}

/**
 * Narrow read of enrollmentQueueSource's EnrollmentQueueDetail — only the
 * field the Queued fact card needs (`ownerName`), defensively parsed.
 * Missing/malformed detail parses to `null` — the card falls back to
 * "In queue" with no owner clause (never a guessed name).
 */
const QueuedCardDetailSchema = z.object({ ownerName: z.string().nullable() }).partial();

export function queuedCardOwnerName(detail: unknown): string | null {
  const parsed = QueuedCardDetailSchema.safeParse(detail);
  return parsed.success ? parsed.data.ownerName ?? null : null;
}

/**
 * The deterministic identity of one `StateFact` — both surfaces (V1's
 * `StatePills`/`StateFactCard` and V2's `EntityStateFactCard`) key their
 * fact lists identically off this, never a hand-rolled format.
 */
export function factCardKey(fact: StateFact): string {
  return `${fact.dimension}-${fact.evidence_ref}-${fact.observed_at}`;
}

/**
 * RENDER limit for the Recent Actions section (flow-workflow-requests,
 * "timestamps + View all") — kept in sync BY HAND with the backend's
 * `RECENT_REQUEST_MAX_PER_CONTACT` (src/services/flow/contact-state-sources.ts),
 * same disclaimer as this module's other lifted constants: core services and
 * this frontend bundle don't share runtime code across that boundary. The
 * backend resolves up to `RECENT_REQUEST_FETCH_LIMIT` (3) facts specifically
 * so a consumer can detect "a 3rd exists" from the array length alone, with
 * no separate count endpoint.
 *
 * V1's own copy of this constant stays put in `ContactDetailPanels.tsx` (not
 * imported from here) — this is a second, independent copy for V2 consumers,
 * per the U1c bead's explicit instruction to leave the V1 constant in place.
 */
export const RECENT_REQUEST_RENDER_LIMIT = 2;
